/**
 * AD ↔ LP Alignment Page
 *
 * Two-panel flow:
 * 1. Campaign list with summary metrics (default view)
 * 2. Click a campaign → drill into its landing pages with funnel metrics
 */

import { format } from 'date-fns'
import { useState, useEffect, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { trafficAPI } from '../services/api'
import {
  Card, CardContent, CardHeader, CardTitle,
  Button
} from '../components/ui'
import {
  RefreshCw, Loader2, AlertCircle, AlertTriangle, Search, X, Filter, ArrowLeft
} from 'lucide-react'
import { DateRangePicker } from '../components/DateRangePicker'

function formatNum(v) {
  if (v == null || isNaN(v)) return '-'
  return new Intl.NumberFormat('en-US').format(v)
}

function formatPct(v) {
  if (v == null || isNaN(v)) return '-'
  return v.toFixed(1) + '%'
}

function formatCurrency(v) {
  if (v == null || isNaN(v)) return '-'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v)
}

const LANE_COLORS = {
  glp1: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  semaglutide: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  tirzepatide: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
  phentermine: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  trt: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  hair: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  ed: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
  unmapped: 'bg-muted text-muted-foreground',
}

function LaneBadge({ lane, name, mismatch }) {
  const color = LANE_COLORS[lane] || 'bg-muted text-muted-foreground'
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium ${color}`}>
      {mismatch && <AlertTriangle className="h-3 w-3 text-yellow-500" />}
      {name || lane}
    </span>
  )
}

export default function AdLpAlignmentPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedCampaign, setSelectedCampaign] = useState(null)
  const [adFilter, setAdFilter] = useState(null) // filter by adset or ad within drilldown
  const [searchInput, setSearchInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [filter, setFilter] = useState('all')

  const now = new Date()
  const defaultFrom = new Date(now); defaultFrom.setDate(now.getDate() - 30)

  const [dateRange, setDateRange] = useState(() => {
    const s = searchParams.get('start')
    const e = searchParams.get('end')
    if (s && e) {
      return { from: new Date(s + 'T12:00:00'), to: new Date(e + 'T12:00:00') }
    }
    return { from: defaultFrom, to: now }
  })

  const startDate = format(dateRange.from, 'yyyy-MM-dd')
  const endDate = format(dateRange.to, 'yyyy-MM-dd')

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      setSelectedCampaign(null)
      const res = await trafficAPI.getAdLpAlignment({ start: startDate, end: endDate })
      if (res.data.success) setData(res.data.data)
    } catch (err) {
      console.error('[AD-LP] Error:', err)
      setError(err.response?.data?.error?.message || err.message)
    } finally {
      setLoading(false)
    }
  }, [startDate, endDate])

  useEffect(() => { fetchData() }, [fetchData])

  useEffect(() => {
    setSearchParams({ start: startDate, end: endDate }, { replace: true })
  }, [startDate, endDate, setSearchParams])

  const handleDateChange = useCallback((newRange) => {
    if (newRange?.from && newRange?.to) setDateRange(newRange)
  }, [])

  const summary = data?.summary

  // Group by campaign
  const campaigns = useMemo(() => {
    const map = {}
    for (const r of (data?.rows || [])) {
      const key = r.campaign
      if (!map[key]) map[key] = {
        campaign: key, source: r.source, campaign_lane: r.campaign_lane,
        campaign_lane_name: r.campaign_lane_name,
        visitors: 0, intake_starts: 0, emails: 0, payments: 0, revenue: 0, spend: null,
        lps: [], mismatch_visitors: 0,
      }
      map[key].visitors += r.visitors
      map[key].intake_starts += r.intake_starts || 0
      map[key].emails += r.emails
      map[key].payments += r.payments
      map[key].revenue += r.revenue || 0
      if (r.spend != null) map[key].spend = (map[key].spend || 0) + r.spend
      if (r.mismatch) map[key].mismatch_visitors += r.visitors
      map[key].lps.push(r)
    }
    return Object.values(map)
      .map(c => ({
        ...c,
        lp_count: c.lps.length,
        lp_click_rate: c.visitors > 0 ? (c.intake_starts / c.visitors) * 100 : 0,
        cvr: c.visitors > 0 ? (c.payments / c.visitors) * 100 : 0,
        email_rate: c.visitors > 0 ? (c.emails / c.visitors) * 100 : 0,
        has_mismatch: c.mismatch_visitors > 0,
        cpa: c.payments > 0 && c.spend ? c.spend / c.payments : null,
      }))
      .sort((a, b) => b.visitors - a.visitors)
  }, [data])

  // Filter campaigns
  const filteredCampaigns = useMemo(() => {
    let list = campaigns
    if (filter === 'mismatch') list = list.filter(c => c.has_mismatch)
    if (filter === 'mapped') list = list.filter(c => c.campaign_lane !== 'unmapped')
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      list = list.filter(c =>
        c.campaign?.toLowerCase().includes(q) ||
        c.source?.toLowerCase().includes(q) ||
        c.campaign_lane_name?.toLowerCase().includes(q)
      )
    }
    return list
  }, [campaigns, filter, searchQuery])

  // LP rows for selected campaign, optionally filtered by ad/adset
  const selectedLps = useMemo(() => {
    if (!selectedCampaign) return []
    const c = campaigns.find(c => c.campaign === selectedCampaign)
    let lps = c?.lps || []
    if (adFilter) {
      lps = lps.filter(r => r.adset_name === adFilter || r.ad_name === adFilter || r.utm_content === adFilter)
    }
    // Aggregate by landing_page + lp_lane (since we now have utm_content granularity)
    const lpMap = {}
    for (const r of lps) {
      const key = r.landing_page + '|' + r.lp_lane
      if (!lpMap[key]) lpMap[key] = { ...r, visitors: 0, intake_starts: 0, emails: 0, payments: 0, revenue: 0 }
      lpMap[key].visitors += r.visitors
      lpMap[key].intake_starts += r.intake_starts || 0
      lpMap[key].emails += r.emails
      lpMap[key].payments += r.payments
      lpMap[key].revenue += r.revenue || 0
    }
    return Object.values(lpMap)
      .map(r => ({ ...r, lp_click_rate: r.visitors > 0 ? (r.intake_starts / r.visitors) * 100 : 0, email_rate: r.visitors > 0 ? (r.emails / r.visitors) * 100 : 0, cvr: r.visitors > 0 ? (r.payments / r.visitors) * 100 : 0 }))
      .sort((a, b) => b.visitors - a.visitors)
  }, [selectedCampaign, campaigns, adFilter])

  const selectedCampaignData = campaigns.find(c => c.campaign === selectedCampaign)

  // Build adset → ads tree within selected campaign
  const adTree = useMemo(() => {
    if (!selectedCampaign) return []
    const c = campaigns.find(c => c.campaign === selectedCampaign)
    const adsetMap = {}
    for (const r of (c?.lps || [])) {
      const adsetName = r.adset_name || '(no adset)'
      const adName = r.ad_name || r.utm_content || '(no ad)'
      if (!adsetMap[adsetName]) adsetMap[adsetName] = { name: adsetName, visitors: 0, ads: {} }
      adsetMap[adsetName].visitors += r.visitors
      if (!adsetMap[adsetName].ads[adName]) adsetMap[adsetName].ads[adName] = { name: adName, utm_content: r.utm_content, visitors: 0 }
      adsetMap[adsetName].ads[adName].visitors += r.visitors
    }
    return Object.values(adsetMap)
      .map(as => ({ ...as, ads: Object.values(as.ads).sort((a, b) => b.visitors - a.visitors) }))
      .sort((a, b) => b.visitors - a.visitors)
  }, [selectedCampaign, campaigns])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">AD ↔ LP Alignment</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {selectedCampaign ? 'Landing page breakdown for campaign' : 'Campaign-to-landing-page mapping with lane mismatch detection'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DateRangePicker dateRange={dateRange} onDateRangeChange={handleDateChange} />
          <Button onClick={fetchData} variant="outline" size="sm" disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {error && (
        <Card className="border-destructive"><CardContent className="pt-6">
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="w-5 h-5" /><span>{error}</span>
          </div>
        </CardContent></Card>
      )}

      {loading && !data && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {data && !selectedCampaign && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            <Card><CardContent className="pt-4 pb-3">
              <span className="text-xs text-muted-foreground">Campaigns</span>
              <p className="text-xl font-bold">{formatNum(campaigns.length)}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4 pb-3">
              <span className="text-xs text-muted-foreground">Visitors</span>
              <p className="text-xl font-bold">{formatNum(summary?.total_visitors)}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4 pb-3">
              <span className="text-xs text-muted-foreground">Payments</span>
              <p className="text-xl font-bold">{formatNum(summary?.total_payments)}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4 pb-3">
              <span className="text-xs text-muted-foreground">Revenue</span>
              <p className="text-xl font-bold">{formatCurrency(summary?.total_revenue)}</p>
            </CardContent></Card>
            <Card className={summary?.mismatches > 0 ? 'border-yellow-300 dark:border-yellow-800' : ''}>
              <CardContent className="pt-4 pb-3">
                <span className="text-xs text-muted-foreground">Mismatched Combos</span>
                <p className={`text-xl font-bold ${summary?.mismatches > 0 ? 'text-yellow-600' : 'text-green-600'}`}>
                  {formatNum(summary?.mismatches)}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Search + Filter */}
          <Card><CardContent className="py-3 px-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <form onSubmit={(e) => { e.preventDefault(); setSearchQuery(searchInput) }} className="flex-1 flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="text"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Search campaigns..."
                    className="w-full pl-9 pr-8 py-1.5 text-sm border rounded-md bg-background"
                  />
                  {searchInput && (
                    <button type="button" onClick={() => { setSearchInput(''); setSearchQuery('') }} className="absolute right-2 top-1/2 -translate-y-1/2">
                      <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                    </button>
                  )}
                </div>
                <Button type="submit" size="sm">Search</Button>
              </form>
              <div className="flex items-center gap-1">
                <Filter className="h-4 w-4 text-muted-foreground" />
                {[
                  { value: 'all', label: 'All' },
                  { value: 'mismatch', label: 'Mismatches' },
                  { value: 'mapped', label: 'Mapped' },
                ].map(opt => (
                  <Button key={opt.value} variant={filter === opt.value ? 'default' : 'ghost'} size="sm"
                    onClick={() => setFilter(opt.value)} className="h-7 text-xs">
                    {opt.label}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent></Card>

          {/* Campaign List */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                Campaigns ({filteredCampaigns.length})
                {loading && <Loader2 className="inline w-4 h-4 ml-2 animate-spin" />}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {filteredCampaigns.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">No campaigns match your filters</p>
              ) : (
                <div className="border rounded-lg overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-3 font-medium">Campaign</th>
                        <th className="text-left p-3 font-medium">Source</th>
                        <th className="text-left p-3 font-medium">Lane</th>
                        <th className="text-right p-3 font-medium">Visitors</th>
                        <th className="text-right p-3 font-medium">LP Click</th>
                        <th className="text-right p-3 font-medium">Emails</th>
                        <th className="text-right p-3 font-medium">Payments</th>
                        <th className="text-right p-3 font-medium">CVR</th>
                        <th className="text-right p-3 font-medium">Revenue</th>
                        <th className="text-right p-3 font-medium">Spend</th>
                        <th className="text-right p-3 font-medium">CPA</th>
                        <th className="text-center p-3 font-medium">LPs</th>
                        <th className="p-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCampaigns.map(c => (
                        <tr
                          key={c.campaign}
                          className={`border-t hover:bg-muted/30 cursor-pointer ${c.has_mismatch ? 'bg-yellow-50/30 dark:bg-yellow-950/10' : ''}`}
                          onClick={() => { setSelectedCampaign(c.campaign); setAdFilter(null) }}
                        >
                          <td className="p-3 font-medium text-sm max-w-[350px] break-words">{c.campaign}</td>
                          <td className="p-3 text-xs">{c.source}</td>
                          <td className="p-3"><LaneBadge lane={c.campaign_lane} name={c.campaign_lane_name} /></td>
                          <td className="p-3 text-right font-mono">{formatNum(c.visitors)}</td>
                          <td className="p-3 text-right font-mono">{formatPct(c.lp_click_rate)}</td>
                          <td className="p-3 text-right font-mono">{formatNum(c.emails)}</td>
                          <td className="p-3 text-right font-mono">{formatNum(c.payments)}</td>
                          <td className="p-3 text-right font-mono">{formatPct(c.cvr)}</td>
                          <td className="p-3 text-right font-mono">{formatCurrency(c.revenue)}</td>
                          <td className="p-3 text-right font-mono">{c.spend != null ? formatCurrency(c.spend) : '-'}</td>
                          <td className="p-3 text-right font-mono">{c.cpa != null ? formatCurrency(c.cpa) : '-'}</td>
                          <td className="p-3 text-center">{c.lp_count}</td>
                          <td className="p-3">
                            {c.has_mismatch && <AlertTriangle className="h-4 w-4 text-yellow-500" />}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Campaign Drilldown */}
      {data && selectedCampaign && (
        <>
          <Button variant="ghost" size="sm" onClick={() => { setSelectedCampaign(null); setAdFilter(null) }} className="gap-1">
            <ArrowLeft className="h-4 w-4" /> Back to campaigns
          </Button>

          {/* Campaign header card */}
          {selectedCampaignData && (
            <Card>
              <CardContent className="py-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <h2 className="font-semibold text-lg">{selectedCampaign}</h2>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-muted-foreground">{selectedCampaignData.source}</span>
                      <LaneBadge lane={selectedCampaignData.campaign_lane} name={selectedCampaignData.campaign_lane_name} />
                    </div>
                  </div>
                  <div className="flex gap-4 text-sm">
                    <div className="text-center">
                      <div className="text-xs text-muted-foreground">Visitors</div>
                      <div className="font-bold">{formatNum(selectedCampaignData.visitors)}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-muted-foreground">Emails</div>
                      <div className="font-bold">{formatNum(selectedCampaignData.emails)}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-muted-foreground">Payments</div>
                      <div className="font-bold">{formatNum(selectedCampaignData.payments)}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-muted-foreground">CVR</div>
                      <div className="font-bold">{formatPct(selectedCampaignData.cvr)}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-muted-foreground">Revenue</div>
                      <div className="font-bold">{formatCurrency(selectedCampaignData.revenue)}</div>
                    </div>
                    {selectedCampaignData.spend != null && (
                      <div className="text-center">
                        <div className="text-xs text-muted-foreground">Spend</div>
                        <div className="font-bold">{formatCurrency(selectedCampaignData.spend)}</div>
                      </div>
                    )}
                    {selectedCampaignData.cpa != null && (
                      <div className="text-center">
                        <div className="text-xs text-muted-foreground">CPA</div>
                        <div className="font-bold">{formatCurrency(selectedCampaignData.cpa)}</div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Ad/Adset Filter Tree */}
          {adTree.length > 1 || (adTree.length === 1 && adTree[0].ads.length > 1) ? (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between">
                  <span>Filter by Ad / Adset</span>
                  {adFilter && (
                    <Button variant="ghost" size="sm" onClick={() => setAdFilter(null)} className="h-6 text-xs gap-1">
                      <X className="h-3 w-3" /> Clear filter
                    </Button>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="border rounded-lg divide-y max-h-[300px] overflow-y-auto">
                  {/* All option */}
                  <button
                    onClick={() => setAdFilter(null)}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors ${!adFilter ? 'bg-primary/10 font-medium' : 'hover:bg-muted/50'}`}
                  >
                    All ads
                  </button>
                  {adTree.map(adset => (
                    <div key={adset.name}>
                      {/* Adset row */}
                      <button
                        onClick={() => setAdFilter(adset.name)}
                        className={`w-full text-left px-3 py-2 transition-colors ${adFilter === adset.name ? 'bg-primary/10' : 'hover:bg-muted/50'}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium break-words">{adset.name}</span>
                          <span className="text-xs text-muted-foreground flex-shrink-0">{formatNum(adset.visitors)}</span>
                        </div>
                      </button>
                      {/* Individual ads under this adset */}
                      {adset.ads.length > 1 && adset.ads.map(ad => (
                        <button
                          key={ad.name}
                          onClick={() => setAdFilter(ad.name)}
                          className={`w-full text-left pl-6 pr-3 py-1.5 transition-colors ${adFilter === ad.name ? 'bg-primary/10' : 'hover:bg-muted/50'}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs text-muted-foreground break-words">{ad.name}</span>
                            <span className="text-[10px] text-muted-foreground flex-shrink-0">{formatNum(ad.visitors)}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : null}

          {/* LP Detail Table */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Landing Pages ({selectedLps.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {selectedLps.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">No landing page data</p>
              ) : (
                <div className="border rounded-lg overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-3 font-medium">Landing Page</th>
                        <th className="text-left p-3 font-medium">LP Lane</th>
                        <th className="text-center p-3 font-medium">Match</th>
                        <th className="text-right p-3 font-medium">Visitors</th>
                        <th className="text-right p-3 font-medium">LP Click</th>
                        <th className="text-right p-3 font-medium">Emails</th>
                        <th className="text-right p-3 font-medium">Email Rate</th>
                        <th className="text-right p-3 font-medium">Payments</th>
                        <th className="text-right p-3 font-medium">CVR</th>
                        <th className="text-right p-3 font-medium">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedLps.map((r, i) => (
                        <tr key={i} className={`border-t hover:bg-muted/30 ${r.mismatch ? 'bg-yellow-50/50 dark:bg-yellow-950/10' : ''}`}>
                          <td className="p-3 text-xs font-mono max-w-[300px] truncate" title={r.landing_page}>{r.landing_page}</td>
                          <td className="p-3"><LaneBadge lane={r.lp_lane} name={r.lp_lane_name} /></td>
                          <td className="p-3 text-center">
                            {r.mismatch ? (
                              <AlertTriangle className="h-4 w-4 text-yellow-500 mx-auto" title="Lane mismatch" />
                            ) : r.lp_lane !== 'unmapped' ? (
                              <span className="text-green-600 text-xs font-medium">Aligned</span>
                            ) : (
                              <span className="text-muted-foreground text-xs">-</span>
                            )}
                          </td>
                          <td className="p-3 text-right font-mono">{formatNum(r.visitors)}</td>
                          <td className="p-3 text-right font-mono">{formatPct(r.lp_click_rate)}</td>
                          <td className="p-3 text-right font-mono">{formatNum(r.emails)}</td>
                          <td className="p-3 text-right font-mono">{formatPct(r.email_rate)}</td>
                          <td className="p-3 text-right font-mono">{formatNum(r.payments)}</td>
                          <td className="p-3 text-right font-mono">{formatPct(r.cvr)}</td>
                          <td className="p-3 text-right font-mono">{formatCurrency(r.revenue)}</td>
                        </tr>
                      ))}
                      {/* Totals row */}
                      {selectedLps.length > 1 && (
                        <tr className="border-t-2 border-foreground/20 bg-muted/30 font-bold">
                          <td className="p-3 text-xs" colSpan={3}>TOTAL</td>
                          <td className="p-3 text-right font-mono">{formatNum(selectedCampaignData?.visitors)}</td>
                          <td className="p-3 text-right font-mono">{formatPct(selectedCampaignData?.lp_click_rate)}</td>
                          <td className="p-3 text-right font-mono">{formatNum(selectedCampaignData?.emails)}</td>
                          <td className="p-3 text-right font-mono">{formatPct(selectedCampaignData?.email_rate)}</td>
                          <td className="p-3 text-right font-mono">{formatNum(selectedCampaignData?.payments)}</td>
                          <td className="p-3 text-right font-mono">{formatPct(selectedCampaignData?.cvr)}</td>
                          <td className="p-3 text-right font-mono">{formatCurrency(selectedCampaignData?.revenue)}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
